import { faLightbulb, faTrashCan } from "@fortawesome/free-regular-svg-icons";
import {
  faChartLine,
  faHeadset,
  faRobot,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ArrowDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DriveEtaOutlinedIcon from "@mui/icons-material/DriveEtaOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import SortIcon from "@mui/icons-material/Sort";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import VisibilityOutlinedOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { PaletteColor, useTheme } from "@mui/material/styles";

export type APIconName =
  | "play"
  | "download"
  | "calculate"
  | "next"
  | "edit"
  | "help"
  | "refresh"
  | "sort"
  | "visibility"
  | "visibility-off"
  | "lock"
  | "lock-open"
  | "close"
  | "trophy"
  | "arrow-left"
  | "arrow-right"
  | "arrow-down"
  | "arrow-up"
  | "car"
  | "money"
  | "delete"
  | "speedometer"
  | "expand-more"
  | "expand-less"
  | "robot"
  | "lightbulb"
  | "quote"
  | "chart-line"
  | "check-circle"
  | "check"
  | "cancel"
  | "warning"
  | "info";

const APIcon = ({
  name,
  size,
  color,
}: {
  name: APIconName;
  size?: "small" | "large" | "medium";
  color?:
    | "primary"
    | "secondary"
    | "tertiaryGreen"
    | "onSurfaceVariant"
    | "error"
    | "warning";
}) => {
  const theme = useTheme();

  let icon;
  const iconProps = {
    fontSize: size,
    sx: { color: color ? `${color}.main` : undefined },
  };
  const themeColor = theme.palette[
    color as keyof typeof theme.palette
  ] as PaletteColor;
  const cssColor = themeColor?.main;

  switch (name) {
    case "play":
      icon = <PlayArrowOutlinedIcon {...iconProps} />;
      break;
    case "download":
      icon = <DownloadIcon {...iconProps} />;
      break;
    case "calculate":
      icon = <CalculateOutlinedIcon {...iconProps} />;
      break;
    case "next":
      icon = <ArrowForwardOutlinedIcon {...iconProps} />;
      break;
    case "edit":
      icon = <EditOutlinedIcon {...iconProps} />;
      break;
    case "help":
      icon = <FontAwesomeIcon icon={faHeadset} color={cssColor} size="xl" />;
      break;
    case "refresh":
      icon = <AutorenewIcon {...iconProps} />;
      break;
    case "sort":
      icon = <SortIcon {...iconProps} />;
      break;
    case "visibility":
      icon = <VisibilityOutlinedIcon {...iconProps} />;
      break;
    case "visibility-off":
      icon = <VisibilityOutlinedOffIcon {...iconProps} />;
      break;
    case "lock":
      icon = <LockOutlinedIcon {...iconProps} />;
      break;
    case "lock-open":
      icon = <LockOpenOutlinedIcon {...iconProps} />;
      break;
    case "close":
      icon = <CloseOutlinedIcon {...iconProps} />;
      break;
    case "trophy":
      icon = <EmojiEventsIcon {...iconProps} />;
      break;
    case "arrow-left":
      icon = <ArrowLeftIcon {...iconProps} />;
      break;
    case "arrow-right":
      icon = <ArrowRightIcon {...iconProps} />;
      break;
    case "arrow-down":
      icon = <ArrowDownIcon {...iconProps} />;
      break;
    case "arrow-up":
      icon = <ArrowUpIcon {...iconProps} />;
      break;
    case "car":
      icon = <DriveEtaOutlinedIcon {...iconProps} />;
      break;
    case "money":
      icon = <AttachMoneyIcon {...iconProps} />;
      break;
    case "delete":
      icon = (
        <FontAwesomeIcon
          icon={faTrashCan}
          color={cssColor}
          width={16}
          height={16}
          style={{ marginLeft: 4, marginRight: 4 }}
        />
      );
      break;
    case "speedometer":
      icon = <SpeedOutlinedIcon {...iconProps} />;
      break;
    case "expand-more":
      icon = <ExpandMoreIcon {...iconProps} />;
      break;
    case "expand-less":
      icon = <ExpandLessIcon {...iconProps} />;
      break;
    case "robot":
      icon = <FontAwesomeIcon icon={faRobot} color={cssColor} size="xl" />;
      break;
    case "lightbulb":
      icon = <FontAwesomeIcon icon={faLightbulb} color={cssColor} size="xl" />;
      break;
    case "quote":
      icon = <RequestQuoteOutlinedIcon {...iconProps} />;
      break;
    case "chart-line":
      icon = <FontAwesomeIcon icon={faChartLine} color={cssColor} size="xl" />;
      break;
    case "check-circle":
      icon = <CheckCircleOutlineOutlinedIcon {...iconProps} />;
      break;
    case "check":
      icon = <CheckIcon {...iconProps} />;
      break;
    case "cancel":
      icon = <CancelIcon {...iconProps} />;
      break;
    case "warning":
      icon = <WarningAmberOutlinedIcon {...iconProps} />;
      break;
    case "info":
      icon = <InfoOutlinedIcon {...iconProps} />;
      break;
  }
  return icon || <></>;
};

export default APIcon;
